import { useEffect, useCallback, useMemo, useState } from 'react';
import { useRouteMatch, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import has from 'lodash/has';

import { getConnectedContent } from '../ConnectedContentService';

const getEmptyLinkedConnections = () => ({
  connectedKfas: [],
  connectedInsights: [],
  connectedAnalyses: [],
});

const useConnectedContent = (contentId) => {
  const { url } = useRouteMatch();
  const { projectId } = useParams();

  const [linkedConnections, setLinkedConnections] = useState(
    getEmptyLinkedConnections()
  );

  const contentTypes = useMemo(() => {
    if (url.includes('key-focus-areas')) {
      return ['analyses'];
    }
    return ['hypotheses', 'analyses'];
  }, [url]);

  const processConnectedContent = useCallback(
    (retrievedConnectedContent) => {
      if (contentId) {
        const editedConnections = cloneDeep(retrievedConnectedContent);
        if (has(editedConnections, contentTypes[0])) {
          const keysToRemove = Object.keys(editedConnections).filter(
            (key) => contentTypes.indexOf(key) < 0
          );
          keysToRemove.forEach((key) => {
            delete editedConnections[key];
          });
        }
        return editedConnections;
      }
      return [];
    },
    [contentTypes, contentId]
  );

  useEffect(() => {
    const fetchConnectedContent = async () => {
      const fetchedConnectedContent = await getConnectedContent(
        projectId,
        contentId
      );

      const processedConnections = processConnectedContent(
        fetchedConnectedContent
      );
      const hasConnectedContent =
        processedConnections &&
        Object.values(processedConnections).some((_) => _.length);

      if (hasConnectedContent) {
        setLinkedConnections({
          connectedAnalyses: processedConnections.analyses,
          connectedKfas: processedConnections.hypotheses,
        });
      } else {
        setLinkedConnections(getEmptyLinkedConnections());
      }
    };
    if (projectId && contentId) {
      fetchConnectedContent();
    }
    return () => {
      setLinkedConnections(getEmptyLinkedConnections());
    };
  }, [projectId, contentId, processConnectedContent]);

  return linkedConnections;
};

export default useConnectedContent;
